import React, { Context, ReactNode, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { uuid } from 'uuidv4';
import LogRocket from 'logrocket';
import { getInnerText } from '@lukaspolak/react-get-inner-text';

import { shouldEnableLogRocket } from '@trqk/utils';

import { Toast } from './Toast';

/**
 * @typedef {ToastProviderProps} Configuration for a ToastProvider. Note that there are two separate
 * div IDs passed in: toastBannerRoot and toastCardRoot. There must be two distinct, sibling <div>
 * elements in the index.html with these IDs. This is because banners render differently than cards.
 * @property {ReactNode} children
 * @property {Context<ToastProviderValue>} ToastContext
 * @property {string} toastBannerRootId the ID of the DOM element where the toast banners will be
 * mounted
 * @property {string} toastCardRootId the ID of the DOM element where the toast cards will be
 * mounted
 */
interface ToastProviderProps {
    children?: ReactNode;
    ToastContext: Context<ToastProviderValue>;
    toastBannerRootId: string;
    toastCardRootId: string;
}

const defaultToastDuration = 5000;

export const toastPortalClasses =
    'relative z-50 top-0 left-0 w-screen flex flex-col items-center';

const enableLogRocket = shouldEnableLogRocket();

/**
 * Provides global access to functions for adding toast notifications.
 * Note that when adding a toast, you have the option to supply either `content` or `RenderContent`.
 * The difference is that `content` is assumed to be already rendered as a string or JSX element,
 * whereas RenderContent should be function that returns an element. RenderContent will be passed
 * the `remove` function, making it possible to remove the toast in response to some user action.
 *
 * If you call toasts.add() with a config object that has .persist === true, the toast will not
 * be removed until the user closes it manually.
 *
 * A toast can either be a ToastBanner or ToastCard - see those docs for the visual differences. If
 * the `kind` is either 'info-banner' or 'warning-banner', a ToastBanner is used. If the kind is
 * either 'info-card' or 'warning-card', a ToastCard is used.
 *
 * If there is more than one ToastBanner, they will stack vertically at the top of the screen,
 * whereas only the last ToastCard added to the stack will be shown. If the card is set with
 * `position: 'center'`, then the rest of the screen will become unresponsive. Use this
 * configuration when you want to force the user to make a choice before proceeding.
 *
 * @param props {ToastProviderProps}
 */
export const ToastProvider = ({
    children,
    ToastContext,
    toastBannerRootId,
    toastCardRootId,
}: ToastProviderProps): JSX.Element => {
    const [toasts, setToasts] = useState([] as ToastType<unknown>[]);

    const addToast = useCallback((input: AddToastInput<unknown>): void => {
        const {
            id = uuid(),
            content,
            duration = defaultToastDuration,
            kind,
            onRemove,
            persist,
            position,
            RenderContent,
            renderContentInput,
        } = input;

        // If an id is provided, only create a new toast if a toast with the provided id does not
        // exist yet. This is important if toast.add() is triggered by React reducer.
        // For an example, see: packages/web/src/hooks/useTasks/TasksProvider.tsx
        if (
            !toasts.some(
                (toast: ToastType<unknown>): boolean => toast.id === id,
            )
        ) {
            if (
                enableLogRocket &&
                (kind === 'warning-banner' || kind === 'warning-card')
            ) {
                let { logMessage } = input;

                if (logMessage === undefined) {
                    const innerText = getInnerText(content);

                    logMessage =
                        innerText.length > 0 ? innerText : RenderContent?.name;
                }

                if (logMessage) {
                    LogRocket.captureMessage(logMessage, {
                        tags: {
                            component: 'Toast',
                            toastKind: kind,
                            level: 'warning',
                        },
                    });
                }
            }

            setToasts(
                (prevToasts: ToastType<unknown>[]): ToastType<unknown>[] => [
                    ...prevToasts,
                    {
                        id,
                        content,
                        duration,
                        kind,
                        onRemove,
                        persist,
                        position,
                        RenderContent,
                        renderContentInput,
                    },
                ],
            );
        }
    }, []);

    const removeToast = useCallback((id: string): void => {
        setToasts((prevToasts: ToastType<unknown>[]): ToastType<unknown>[] =>
            prevToasts.filter(
                (toast: ToastType<unknown>): boolean => toast.id !== id,
            ),
        );
    }, []);

    let toastBannerRoot = document.getElementById(toastBannerRootId);

    if (toastBannerRoot === null) {
        toastBannerRoot = document.body;
    }

    let toastCardRoot = document.getElementById(toastCardRootId);

    if (toastCardRoot === null) {
        toastCardRoot = document.body;
    }

    const banners = toasts.filter(
        ({ kind }: ToastType<unknown>): boolean =>
            kind === 'info-banner' || kind === 'warning-banner',
    );

    const cards = toasts.filter(
        ({ kind }: ToastType<unknown>): boolean =>
            kind === 'info-card' || kind === 'warning-card',
    );

    const lastCard = cards[cards.length - 1];
    let renderLastCard = null;

    if (lastCard) {
        const {
            id,
            content,
            duration,
            kind,
            onRemove,
            RenderContent,
            renderContentInput,
            persist,
            position,
        } = lastCard;

        renderLastCard = (
            <Toast
                key={id}
                kind={kind}
                remove={(input?: RemoveToastInput): void => {
                    removeToast(id);

                    if (onRemove) {
                        onRemove(input);
                    }
                }}
                duration={duration}
                content={content}
                RenderContent={RenderContent}
                renderContentInput={renderContentInput}
                persist={persist}
                position={position}
            />
        );
    }

    return (
        <ToastContext.Provider value={{ add: addToast, remove: removeToast }}>
            {createPortal(
                <div className={toastPortalClasses}>
                    {banners.map(
                        ({
                            id,
                            content,
                            duration,
                            kind,
                            onRemove,
                            RenderContent,
                            renderContentInput,
                            persist,
                            position,
                        }: ToastType<unknown>): JSX.Element => (
                            <Toast
                                key={id}
                                kind={kind}
                                remove={(input?: RemoveToastInput): void => {
                                    removeToast(id);

                                    if (onRemove) {
                                        onRemove(input);
                                    }
                                }}
                                duration={duration}
                                content={content}
                                RenderContent={RenderContent}
                                renderContentInput={renderContentInput}
                                persist={persist}
                                position={position}
                            />
                        ),
                    )}
                </div>,
                toastBannerRoot,
            )}
            {createPortal(
                <div className={toastPortalClasses}>{renderLastCard}</div>,
                toastCardRoot,
            )}

            {children}
        </ToastContext.Provider>
    );
};
